var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageSizeTypeEnum, ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import { Image } from '@inter-ikea-kompis/component-image';
import { ImageAspectRatioEnum } from '@inter-ikea-kompis/component-image';
import { MediaCarousel } from '@inter-ikea-kompis/component-media-carousel';
import ProductCardMediaEnum from '../../enums/ProductCardMediaEnum.js';
import ProductCardMediaStyle from './ProductCardMediaStyle.js';
/**
 * Product Card component.
 *
 * @event carouselChange Triggered when a carousel slide should be changed.
 * @event carouselShowAllThumbnails Triggered when the show all thumbnails button is clicked in the carousel.
 */
export default class ProductCardMedia extends KompisElement {
    constructor() {
        super(...arguments);
        // Sets how media should be displayed.
        this.media = ProductCardMediaEnum.singleImage;
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Selected index of the carousel slides.
        this.carouselSelectedIndex = 0;
        // Max limit of thumbnails to be displayed in the carousel.
        this.carouselThumbnailLimit = null;
        // Shows all thumbnails in the carousel when a limit has been set.
        this.carouselShowAll = false;
        // Show scrollbar representing pageIndex
        this.carouselShowScrollbar = false;
        // Show arrows in carousel.
        this.carouselShowArrows = false;
        // "true" if hovered.
        this.hovered = false;
        // The aspect ratio that the images should adopt.
        this.imageAspectRatio = ImageAspectRatioEnum.ORIGINAL;
        // Defines the image sort order. When only one image is being displayed, the first image in the list is used.
        this.imageSortOrder = null;
        // Defines the image sort order for the image displayed on hover when "media" is set to "switchImageOnHover".
        this.hoverImageSortOrder = [
            ImageTypeNameEnum.functionalProductPicture,
            ImageTypeNameEnum.contextProductPicture
        ];
    }
    /**
     * Render.
     *
     * @returns Template.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        if (!this.product?.content) {
            return null;
        }
        return html `
			<style>
				${ProductCardMediaStyle.getStyle({
            theme: this.theme,
            isHovered: this.hovered,
            hasHoverImage: !!this.getHoverImage()
        })}
			</style>
			${this.getContainer()}
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-image', Image);
        this.customElements.define('kompis-media-carousel', MediaCarousel);
        super.connectedCallback();
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    /**
     * Returns container.
     *
     * @returns Template.
     */
    getContainer() {
        return html ` <div class="container">${this.getMedia()}</div> `;
    }
    /**
     * Returns media.
     *
     * @returns Template.
     */
    getMedia() {
        switch (this.media) {
            case ProductCardMediaEnum.singleImage:
            case ProductCardMediaEnum.switchImageOnHover:
                return this.getImage();
            case ProductCardMediaEnum.carousel:
                return this.getCarousel();
            default:
                return null;
        }
    }
    /**
     * Returns single image.
     *
     * @returns Template.
     */
    getImage() {
        if (!this.product?.content?.image?.length) {
            return null;
        }
        const productImage = this.getFirstImageMatchingType(this.product.content.image, this.imageSortOrder ?? null) ||
            this.product.content.image.find((image) => image.size === ImageSizeTypeEnum.S5) ||
            null;
        if (!productImage) {
            return null;
        }
        return html `
			<div class="imageContainer">
				<div class="singleImage">
					<kompis-image
						.src="${productImage.url}"
						.productImage="${true}"
						.aspectRatio="${this.imageAspectRatio}"
					></kompis-image>
				</div>
				${this.getHoverImage()}
			</div>
		`;
    }
    /**
     * Returns hover image.
     *
     * @returns Template.
     */
    getHoverImage() {
        if (this.media !== ProductCardMediaEnum.switchImageOnHover) {
            return null;
        }
        const hoverImage = this.getFirstImageMatchingType(this.product.content?.image ?? null, this.hoverImageSortOrder ?? null);
        if (!hoverImage) {
            return null;
        }
        return html `
			<div class="hoverImage">
				<kompis-image
					.src="${hoverImage.url}"
					.productImage="${true}"
					.aspectRatio="${this.imageAspectRatio}"
				></kompis-image>
			</div>
		`;
    }
    /**
     * Returns carousel.
     *
     * @returns Template.
     */
    getCarousel() {
        if (!this.product?.content?.image) {
            return null;
        }
        const images = this.getImagesSortedByType(this.product.content.image, this.imageSortOrder ?? null);
        const mediaItems = images.map((image) => ({
            src: image.url,
            alt: this.product?.content?.name,
            productImage: true
        }));
        if (mediaItems.length === 0) {
            return null;
        }
        return html `
			<div class="carousel">
				<kompis-media-carousel
					@change="${(event) => this.dispatchEvent(new CustomEvent('carouselChange', {
            bubbles: true,
            detail: {
                carouselSelectedIndex: event.detail.selectedIndex,
                type: event.detail.type
            }
        }))}"
					@showAllThumbnails="${(event) => this.dispatchEvent(new CustomEvent('carouselShowAllThumbnails', {
            bubbles: true,
            detail: { carouselShowAll: event.detail.showAll }
        }))}"
					.theme="${this.theme}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.skipListingLabel="${this.getTranslations().carouselSkipListing}"
					.showLessLabel="${this.getTranslations().carouselShowLess}"
					.mediaItems="${mediaItems}"
					.selectedIndex="${this.carouselSelectedIndex}"
					.thumbnailLimit="${this.carouselThumbnailLimit}"
					.showAll="${this.carouselShowAll}"
					.showScrollbar="${this.carouselShowScrollbar}"
					.showArrows="${this.carouselShowArrows}"
					.aspectRatio="${this.imageAspectRatio}"
				></kompis-media-carousel>
			</div>
		`;
    }
    /**
     * Returns images sorted by type.
     *
     * @param images Images.
     * @param types List of types to sort by in priority order.
     * @returns Sorted images.
     */
    getImagesSortedByType(images, types) {
        if (!types) {
            return images.filter((image) => image.size === ImageSizeTypeEnum.S5);
        }
        const result = [];
        // Add images to start of result based on types order.
        for (const type of types) {
            for (const image of images) {
                if (image.size === ImageSizeTypeEnum.S5 && image.typeName == type) {
                    result.push(image);
                }
            }
        }
        // Add the rest of the images.
        for (const image of images) {
            if (image.size === ImageSizeTypeEnum.S5 && !types.includes(image.typeName)) {
                result.push(image);
            }
        }
        return result;
    }
    /**
     * Returns first image matching the type.
     *
     * @param images Images.
     * @param types List of types to sort by in priority order.
     * @returns First image matching the type.
     */
    getFirstImageMatchingType(images, types) {
        if (!types || !images || !images.length) {
            return null;
        }
        for (const type of types) {
            for (const image of images) {
                if (image.size === ImageSizeTypeEnum.S5 && image.typeName == type) {
                    return image;
                }
            }
        }
        return null;
    }
}
__decorate([
    property({ type: Object, required: true })
], ProductCardMedia.prototype, "theme", void 0);
__decorate([
    property({ type: Object, required: true })
], ProductCardMedia.prototype, "product", void 0);
__decorate([
    property({ type: String })
], ProductCardMedia.prototype, "media", void 0);
__decorate([
    property({ type: Object })
], ProductCardMedia.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ProductCardMedia.prototype, "translations", void 0);
__decorate([
    property({ type: Number })
], ProductCardMedia.prototype, "carouselSelectedIndex", void 0);
__decorate([
    property({ type: Number })
], ProductCardMedia.prototype, "carouselThumbnailLimit", void 0);
__decorate([
    property({ type: Boolean })
], ProductCardMedia.prototype, "carouselShowAll", void 0);
__decorate([
    property({ type: Boolean })
], ProductCardMedia.prototype, "carouselShowScrollbar", void 0);
__decorate([
    property({ type: Boolean })
], ProductCardMedia.prototype, "carouselShowArrows", void 0);
__decorate([
    property({ type: Boolean })
], ProductCardMedia.prototype, "hovered", void 0);
__decorate([
    property({ type: String })
], ProductCardMedia.prototype, "imageAspectRatio", void 0);
__decorate([
    property({ type: Object })
], ProductCardMedia.prototype, "imageSortOrder", void 0);
__decorate([
    property({ type: Object })
], ProductCardMedia.prototype, "hoverImageSortOrder", void 0);
//# sourceMappingURL=ProductCardMedia.js.map